import type { CallEndpoint, MediaDeviceOption } from '@wtfalch/calls/browser';
import { Button, Icon, Popover } from '@wtfalch/design';
import { useEffect, useState } from 'react';

import {
  DEVICE_KINDS,
  type DeviceKind,
  rememberDevice,
  rememberedDevices,
} from './devicePreference.ts';

const GROUPS: Record<DeviceKind, { title: string; noun: string }> = {
  audioinput: { title: 'Microphone', noun: 'microphone' },
  videoinput: { title: 'Camera', noun: 'camera' },
  audiooutput: { title: 'Speaker', noun: 'speaker' },
};

/** Output switching needs `setSinkId`; without it the speaker list would
 *  offer choices that do nothing. */
function canSwitchOutput(): boolean {
  return typeof HTMLMediaElement !== 'undefined' && 'setSinkId' in HTMLMediaElement.prototype;
}

/**
 * The call's "Devices" control: a popover listing microphones, cameras and
 * speakers by label as toggle buttons, the current one pressed. The SDK does not report which
 * device is live, so "current" is this person's last choice, else the system
 * default, else the first listed. A device with no id (labels hidden until
 * permission is granted) is never current. Choosing one calls `endpoint.useDevice`,
 * remembers it for the next call and closes the popover. The list is read
 * when the popover opens and again on `devicechange` while it is open.
 */
export function DevicePicker({
  endpoint,
  onError,
}: {
  endpoint: CallEndpoint;
  onError: (message: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [devices, setDevices] = useState<MediaDeviceOption[] | null>(null);
  const [chosen, setChosen] = useState<Partial<Record<DeviceKind, string>>>({});

  useEffect(() => {
    if (!open) return;
    let live = true;
    const load = () => {
      endpoint.devices().then(
        (list) => live && setDevices(list),
        () => live && setDevices([]),
      );
    };
    setChosen(rememberedDevices());
    load();
    const media = navigator.mediaDevices;
    media?.addEventListener?.('devicechange', load);
    return () => {
      live = false;
      media?.removeEventListener?.('devicechange', load);
    };
  }, [open, endpoint]);

  const kinds = DEVICE_KINDS.filter((kind) => kind !== 'audiooutput' || canSwitchOutput());
  const groups = kinds
    .map((kind) => ({ kind, options: (devices ?? []).filter((d) => d.kind === kind) }))
    .filter((group) => group.options.length > 0);

  const choose = (kind: DeviceKind, deviceId: string, close: () => void) => {
    close();
    // biome-ignore lint/correctness/useHookAtTopLevel: `useDevice` is the SDK's method name, not a hook
    endpoint.useDevice(kind, deviceId).then(
      () => rememberDevice(kind, deviceId),
      () => onError(`Could not switch the ${GROUPS[kind].noun}. Try again.`),
    );
  };

  return (
    <Popover
      label="Devices"
      placement="top"
      open={open}
      onOpenChange={setOpen}
      trigger={
        <Button kind="default" size="sm" iconOnly aria-label="Devices">
          <Icon name="settings" size="md" />
        </Button>
      }
    >
      {(close) => (
        <div className="flex flex-col min-w-56 max-w-80 p-2">
          {devices === null && <p className="px-3 py-2 text-sm text-muted">Looking for devices</p>}
          {devices !== null && groups.length === 0 && (
            <p className="px-3 py-2 text-sm text-muted">No devices found.</p>
          )}
          {groups.map(({ kind, options }, index) => {
            const current =
              options.find((d) => d.deviceId === chosen[kind])?.deviceId ??
              options.find((d) => d.deviceId === 'default')?.deviceId ??
              options.find((d) => d.deviceId)?.deviceId;
            return (
              <fieldset
                key={kind}
                aria-label={GROUPS[kind].title}
                className={index > 0 ? 'mt-2 pt-2 border-t border-border' : undefined}
              >
                <div className="px-3 py-1 text-xs font-semibold text-muted">
                  {GROUPS[kind].title}
                </div>
                {options.map((d, i) => (
                  <button
                    // Ids can repeat or be empty before permission is granted.
                    // biome-ignore lint/suspicious/noArrayIndexKey: the list is re-read whole, never reordered in place
                    key={`${i}|${d.deviceId}`}
                    type="button"
                    aria-pressed={!!d.deviceId && d.deviceId === current}
                    disabled={!d.deviceId}
                    onClick={() => choose(kind, d.deviceId, close)}
                    className="flex w-full items-center gap-3 px-3 py-2 rounded-lg text-left text-sm hover:surface-panel-2 disabled:opacity-50"
                  >
                    <span className="w-4 flex-none text-muted">
                      {!!d.deviceId && d.deviceId === current && <Icon name="check" size="sm" />}
                    </span>
                    <span className="truncate">{d.label || GROUPS[kind].title}</span>
                  </button>
                ))}
              </fieldset>
            );
          })}
        </div>
      )}
    </Popover>
  );
}
